import { ref, nextTick } from "vue";

import {
    setColorsToStorage,
    getColorsFromStorage,
    getDimmingParamsFromStorage,
    setDimmingParamsToStorage,
    generateColorPalette,
    DEFAULT_DIM_FACTOR,
    DEFAULT_FONT_WEIGHT,
    DEFAULT_BG_WEIGHT,
    DEFAULT_DIM_FACTOR_ICON,
    DEFAULT_DISABLED_BUTTON_PARAMS,
    DEFAULT_DISABLED_BG_PARAMS,
} from "../../Helpers/colorPalette.js";

export function useColorTheme(theme) {
    const initialPalette = getColorsFromStorage();
    const globalColorPalette = ref({ ...initialPalette });

    // Load stored dimming parameters or use defaults
    const storedDimmingParams = getDimmingParamsFromStorage();
    const dimmingParams = ref(
        storedDimmingParams || {
            dimFactor: DEFAULT_DIM_FACTOR,
            fontWeight: DEFAULT_FONT_WEIGHT,
            bgWeight: DEFAULT_BG_WEIGHT,
            dimFactorIcon: DEFAULT_DIM_FACTOR_ICON,
        },
    );

    function saveTheme(primaryColours, customDimmingParams = null) {
        const params = customDimmingParams || dimmingParams.value;

        // If dimming params are the default/initial values, pass null to trigger auto-calculation
        // Otherwise, use the custom values for manual override
        const shouldAutoCalculate =
            !customDimmingParams &&
            params.dimFactor === null &&
            params.fontWeight === null &&
            params.bgWeight === null;

        const colors = generateColorPalette(
            primaryColours,
            undefined, // Use default brightness config
            shouldAutoCalculate ? null : params.dimFactor,
            shouldAutoCalculate ? null : params.fontWeight,
            shouldAutoCalculate ? null : params.bgWeight,
            params.dimFactorIcon || DEFAULT_DIM_FACTOR_ICON,
            DEFAULT_DISABLED_BUTTON_PARAMS,
            DEFAULT_DISABLED_BG_PARAMS,
        );

        globalColorPalette.value = colors;
        setColorsToStorage(colors);
        applyColorsToTheme(colors);
        applyCSSCustomProperties(colors);
    }

    function updateDimmingSettings(
        dimFactor,
        fontWeight,
        bgWeight,
        dimFactorIcon,
    ) {
        // Update dimming parameters
        dimmingParams.value = {
            dimFactor,
            fontWeight,
            bgWeight,
            dimFactorIcon,
        };

        // Save dimming parameters to storage
        setDimmingParamsToStorage(dimmingParams.value);

        // Regenerate the color palette with new dimming settings
        saveTheme(
            {
                primary: globalColorPalette.value.primary,
                secondary: globalColorPalette.value.secondary,
                fontLight: globalColorPalette.value.fontLight,
            },
            dimmingParams.value,
        );
    }

    function applyColorsToTheme(colors) {
        Object.entries(colors).forEach(([key, value]) => {
            // Skip opacity values as they're not colors
            if (
                key !== "inactiveOpacity" &&
                key !== "disassociatedMemberOpacity"
            ) {
                nextTick(() => {
                    if (theme) {
                        theme.themes.value.light.colors[key] = value;
                    }
                });
            }
        });
    }

    function applyCSSCustomProperties(colors) {
        Object.entries(colors).forEach(([key, value]) => {
            // Handle opacity values separately as they're numbers, not colors
            if (key === "inactiveOpacity") {
                document.documentElement.style.setProperty(
                    `--inactive-opacity`,
                    value,
                );
            } else if (key === "disassociatedMemberOpacity") {
                document.documentElement.style.setProperty(
                    `--disassociated-member-opacity`,
                    value,
                );
            } else {
                document.documentElement.style.setProperty(
                    `--v-theme-${key}`,
                    value,
                );
            }
        });
    }

    function initialize() {
        applyColorsToTheme(globalColorPalette.value);
        applyCSSCustomProperties(globalColorPalette.value);
    }

    return {
        initialize,
        saveTheme,
        updateDimmingSettings,
        globalColorPalette,
        dimmingParams,
    };
}
